import { PropsWithChildren, useContext, useRef, useState } from "react";
import { Responsive } from "@songtradr-one/components-frontend";
import { ModalConfirm } from "../../components/modal-confirm/ModalConfirm";
import { ModalConfirmMobile } from "../../components/modal-confirm/ModalConfirm.Mobile";
import {
  ModalConfirmConfig,
  ModalConfirmContext,
  ModalConfirmContextValue,
} from "./ModalConfirmContext.Parts";

export const ModalConfirmProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<ModalConfirmConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = (modalConfig: ModalConfirmConfig): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setConfig(modalConfig);
      setOpen(true);
      resolveRef.current = resolve;
    });
  };

  const settle = (confirmed: boolean) => {
    setOpen(false);
    setLoading(false);
    setConfig(null);
    if (resolveRef.current) {
      resolveRef.current(confirmed);
      resolveRef.current = null;
    }
  };

  const handleConfirm = async () => {
    // Callers that pass `onConfirm` keep the modal open, with its confirm
    // button in a loading state, until the work settles. Without it the modal
    // closes the moment it is confirmed and nothing indicates progress.
    if (!config?.onConfirm) return settle(true);

    setLoading(true);
    try {
      await config.onConfirm();
      settle(true);
    } catch {
      settle(false);
    }
  };

  const handleCancel = () => settle(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    setLoading(false);
    if (!newOpen) {
      setConfig(null);
      if (resolveRef.current) {
        resolveRef.current(config?.dismissBehavior === "confirm");
        resolveRef.current = null;
      }
    }
  };

  const value: ModalConfirmContextValue = { confirm };

  const modalConfirmProps = {
    open,
    title: config?.title || "",
    description: config?.description || "",
    cancelLabel: config?.cancelLabel,
    confirmLabel: config?.confirmLabel || "",
    confirmVariant: config?.confirmVariant,
    loading,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    onOpenChange: handleOpenChange,
    preventDismiss: config?.preventDismiss,
  };

  return (
    <ModalConfirmContext.Provider value={value}>
      {children}
      <Responsive
        mobile={
          <ModalConfirmMobile {...modalConfirmProps}>
            {config?.content}
          </ModalConfirmMobile>
        }
        desktop={
          <ModalConfirm {...modalConfirmProps}>{config?.content}</ModalConfirm>
        }
      />
    </ModalConfirmContext.Provider>
  );
};

export const useModalConfirm = (): ModalConfirmContextValue => {
  const context = useContext(ModalConfirmContext);
  if (!context) {
    throw new Error(
      "useModalConfirm must be used within a ModalConfirmProvider",
    );
  }
  return context;
};
